import { __decorate, __metadata } from "tslib";
import { DestroyRef, inject } from '@angular/core';
import { GetNotificationCountRequest, NotificationRecipientItem, SetReadStateRequest } from '@common/notifications/shared';
import { AccessState, AppMessage } from '@core/frontend-shared';
import { AppRequestService } from '@core/frontend-shared/api';
import { ItemServiceFactory } from '@core/frontend-shared/api';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { NotificationsActions } from './notifications.actions';
import * as i0 from "@angular/core";
import * as i1 from "@core/frontend-shared/api";
import * as i2 from "@ngxs/store";
let NotificationsState = /*#__PURE__*/(() => {
  let NotificationsState = class NotificationsState {
    static unread(state) {
      return state.unread;
    }
    static total(state) {
      return state.total;
    }
    static loadedCount(state) {
      return state.loadedCount;
    }
    static loadingItems(state) {
      return state.loadingItems;
    }
    static items(state) {
      return state.items;
    }
    static rangeInfo(state) {
      return state.rangeInfo;
    }
    constructor(api, itemServiceFactory, store) {
      this.api = api;
      this.store = store;
      this.destroyRef = inject(DestroyRef);
      this.service = itemServiceFactory.useDestroyRef(this.destroyRef).createLazyLoadItemService(NotificationRecipientItem);
      this.service.useCrudEndpoint('public');
    }
    ngxsOnInit(ctx) {
      const alreadyloggedIn = this.store.selectSnapshot(AccessState.loggedIn);
      if (alreadyloggedIn) {
        ctx.dispatch(new NotificationsActions.ReloadCount());
      } else {
        // if user logs in later, enable Reload subscription again.
        this.store.select(AccessState.loggedIn).subscribe(loggedIn => {
          if (this.timeout) clearTimeout(this.timeout);
          if (loggedIn) {
            ctx.dispatch(new NotificationsActions.ReloadCount());
          }
        });
      }
    }
    reloadCount(ctx) {
      this.api.sendRequest(GetNotificationCountRequest, {}).subscribe({
        next: response => {
          const oldState = ctx.getState();
          if (oldState.total !== response.total) {
            // new notification detected!
            if (oldState.reloadOnNewNotifications) ctx.dispatch(new NotificationsActions.ReloadNotifications());
          }
          ctx.patchState({
            unread: response.unread,
            total: response.total,
            loadedCount: true
          });
          this.timeout = setTimeout(() => {
            ctx.dispatch(new NotificationsActions.ReloadCount());
          }, 20 * 60 * 1000); // 20 minutes interval
        },
        error: error => {
          if (error instanceof AppMessage) error.discard();
          ctx.patchState({
            aborted: true
          });
        }
      });
    }
    fetch(ctx) {
      this.service.configurate(3);
      ctx.patchState({
        reloadOnNewNotifications: true,
        loadingItems: true
      });
      this.service.getRangeInfo$().subscribe(rangeInfo => {
        ctx.patchState({
          rangeInfo
        });
      });
      this.service.read().subscribe(items => {
        ctx.patchState({
          items,
          loadingItems: false
        });
      });
    }
    stopFetching(ctx) {
      if (this.rangeInfoSubscription) this.rangeInfoSubscription.unsubscribe();
      if (this.itemsSubscription) this.itemsSubscription.unsubscribe();
      ctx.patchState({
        reloadOnNewNotifications: false,
        items: [],
        rangeInfo: null,
        loadingItems: false
      });
    }
    fetchMore(ctx) {
      if (this.service.canLoadMoreItems() && !ctx.getState().loadingItems) {
        ctx.patchState({
          loadingItems: true
        });
        this.service.loadMoreItems(); // will trigger observable subscribed to in fetch()
      }
    }
    reloadNotifications(ctx) {
      ctx.patchState({
        loadingItems: true
      });
      this.service.refreshAllItems();
    }
    deleteNotification(ctx, action) {
      this.service.crud.delete(action.item).subscribe(result => {
        ctx.dispatch(new NotificationsActions.ReloadCount());
        ctx.dispatch(new NotificationsActions.ReloadNotifications());
      });
    }
    toggleReadNotification(ctx, action) {
      this.api.sendRequest(SetReadStateRequest, {
        id: action.id,
        read: action.hasBeenRead
      }).subscribe(response => {
        ctx.dispatch(new NotificationsActions.ReloadCount());
        ctx.dispatch(new NotificationsActions.ReloadNotifications());
      });
    }
    static {
      this.ɵfac = function NotificationsState_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NotificationsState)(i0.ɵɵinject(i1.AppRequestService), i0.ɵɵinject(i1.ItemServiceFactory), i0.ɵɵinject(i2.Store));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: NotificationsState,
        factory: NotificationsState.ɵfac
      });
    }
  };
  __decorate([Action(NotificationsActions.ReloadCount), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], NotificationsState.prototype, "reloadCount", null);
  __decorate([Action(NotificationsActions.Fetch), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], NotificationsState.prototype, "fetch", null);
  __decorate([Action(NotificationsActions.StopFetching), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], NotificationsState.prototype, "stopFetching", null);
  __decorate([Action(NotificationsActions.FetchMore), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], NotificationsState.prototype, "fetchMore", null);
  __decorate([Action(NotificationsActions.ReloadNotifications), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], NotificationsState.prototype, "reloadNotifications", null);
  __decorate([Action(NotificationsActions.DeleteNotification), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, NotificationsActions.DeleteNotification]), __metadata("design:returntype", void 0)], NotificationsState.prototype, "deleteNotification", null);
  __decorate([Action(NotificationsActions.ToggleReadNotification), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, NotificationsActions.ToggleReadNotification]), __metadata("design:returntype", void 0)], NotificationsState.prototype, "toggleReadNotification", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], NotificationsState, "unread", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], NotificationsState, "total", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], NotificationsState, "loadedCount", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], NotificationsState, "loadingItems", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], NotificationsState, "items", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], NotificationsState, "rangeInfo", null);
  NotificationsState = __decorate([State({
    name: 'notifications',
    defaults: {
      unread: 0,
      total: 0,
      aborted: false,
      loadedCount: false,
      loadingItems: false,
      items: [],
      rangeInfo: null,
      reloadOnNewNotifications: false
    }
  }), __metadata("design:paramtypes", [AppRequestService, ItemServiceFactory, Store])], NotificationsState);
  return NotificationsState;
})();
export { NotificationsState };